import { clearSession } from "@/api/session";

// Em produção a app Vue é servida pelo próprio Spring Boot (mesma origem),
// por isso um caminho relativo (string vazia) funciona em qualquer porta
// configurada — só o "npm run serve" precisa de um valor absoluto, definido
// em .env.development, já que corre num servidor separado do backend.
export const BASE_URL = process.env.VUE_APP_API_BASE_URL || "";

export class HttpError extends Error {
  constructor(public status: number, public data: unknown) {
    super(`HTTP ${status}`);
  }
}

/** Lê o cookie XSRF-TOKEN (escrito pelo Spring Security) para reenviar no
 * header X-XSRF-TOKEN — protecção CSRF exige que quem lê o cookie seja o
 * próprio Vue, não um site de terceiros. */
function lerCookieXsrf(): string | null {
  const match = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function esperar(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Métodos seguros para repetir automaticamente: nunca alteram dados nesta
// app (GET nunca é usado para mutações; PUT é usado em toda a app só para
// leituras/listagens — criar/editar/apagar usa sempre POST/DELETE). Uma
// falha de rede transitória (não uma resposta HTTP de erro) não deve
// aparecer ao utilizador como "não foi possível comunicar com o servidor"
// sem pelo menos tentar de novo primeiro.
const METODOS_SEGUROS_PARA_REPETIR = new Set(["GET", "PUT"]);
const MAX_TENTATIVAS_REDE = 2;

export class Http {
  private static async request<T>(
    path: string,
    init: RequestInit,
    timeoutMs = 15000,
  ): Promise<T> {
    const method = (init.method ?? "GET").toUpperCase();
    const podeRepetir = METODOS_SEGUROS_PARA_REPETIR.has(method);

    const headers = new Headers(init.headers);
    const csrfToken = lerCookieXsrf();
    if (csrfToken) headers.set("X-XSRF-TOKEN", csrfToken);

    let response: Response | undefined;
    for (let tentativa = 1; tentativa <= MAX_TENTATIVAS_REDE; tentativa++) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), timeoutMs);
      try {
        response = await fetch(`${BASE_URL}${path}`, {
          ...init,
          headers,
          credentials: "include",
          signal: controller.signal,
        });
        break;
      } catch (erroRede) {
        // Falha de transporte (timeout, ligação perdida, etc.) — não uma
        // resposta HTTP. Só vale a pena repetir em métodos seguros, e só
        // se ainda sobrarem tentativas; senão deixa propagar.
        if (!podeRepetir || tentativa === MAX_TENTATIVAS_REDE) throw erroRede;
        await esperar(400 * tentativa);
      } finally {
        clearTimeout(timeout);
      }
    }
    // response é sempre definido aqui: ou obtido no try, ou já lançámos.
    const respostaFinal = response as Response;

    const data = await respostaFinal.json().catch(() => null);

    if (respostaFinal.status === 401) {
      clearSession();
    }
    if (!respostaFinal.ok) {
      throw new HttpError(respostaFinal.status, data);
    }
    return data as T;
  }

  static get<T>(path: string): Promise<T> {
    return Http.request<T>(path, { method: "GET" });
  }

  static post<T>(
    path: string,
    params?: Record<string, string> | URLSearchParams,
    timeoutMs?: number,
  ): Promise<T> {
    return Http.request<T>(
      path,
      {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params ? new URLSearchParams(params) : undefined,
      },
      timeoutMs,
    );
  }

  static put<T>(path: string, params?: Record<string, string>): Promise<T> {
    return Http.request<T>(path, {
      method: "PUT",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params ? new URLSearchParams(params) : undefined,
    });
  }

  static delete<T>(path: string): Promise<T> {
    return Http.request<T>(path, { method: "DELETE" });
  }

  static postMultipart<T>(
    path: string,
    formData: FormData,
    timeoutMs?: number,
  ): Promise<T> {
    return Http.request<T>(path, { method: "POST", body: formData }, timeoutMs);
  }
}
